import { useEffect, useMemo, useState } from "react";

import { useUpdateFacilityUserAlertSettings } from "./api/useUpdateFacilityUserAlertSettings";
import { HOME_HEALTH_ALERT_ROWS, MASTER_ALERT_KEY } from "./constants";
import { HomeHealthNotificationPreferencesView } from "./HomeHealthNotificationPreferencesView";
import {
  type AlertChannel,
  type AlertChannelEdits,
  type HomeHealthAlertEnabledMap,
  type HomeHealthUser,
  type SavingTarget,
} from "./types";
import { buildMergedAlertSettings, getChannelRowEnabled } from "./utils/buildMergedAlertSettings";

interface HomeHealthNotificationPreferencesContainerProps {
  /**
   * The user whose preferences are being edited. Callers pass already-loaded
   * data (from useGetCurrentUser, the team-members list, or Redux) so this
   * container works for any signed-in role — workplace users cannot read
   * /facilityUser/{id} for themselves. Pass `undefined` while loading; the
   * Container will render its own spinner.
   */
  user: HomeHealthUser | undefined;
  workplaceId: string;
  performedBy?: string;
  onSaved?: () => void;
}

const SMS_ROWS = HOME_HEALTH_ALERT_ROWS.filter(
  (row): row is typeof row & { smsAlertKey: string } => row.smsAlertKey !== undefined
);

export function HomeHealthNotificationPreferencesContainer(
  props: HomeHealthNotificationPreferencesContainerProps
) {
  const { user, workplaceId, performedBy, onSaved } = props;

  const updateAlertSettings = useUpdateFacilityUserAlertSettings();

  const [savingTarget, setSavingTarget] = useState<SavingTarget | undefined>(undefined);

  // Overlay so toggles don't visually revert before the parent's data source
  // (e.g. the team-members list's cached member snapshot) catches up.
  const [optimisticEdits, setOptimisticEdits] = useState<AlertChannelEdits>({});

  // Drop optimistic edits that the parent has caught up on. Return the same
  // reference when nothing changed so React skips the re-render.
  useEffect(() => {
    const persisted = user?.alertSettings;
    if (!persisted) {
      return;
    }
    setOptimisticEdits((current) => {
      let didChange = false;
      const next: AlertChannelEdits = {};
      for (const [channel, channelEdits] of Object.entries(current) as Array<
        [AlertChannel, HomeHealthAlertEnabledMap | undefined]
      >) {
        const remaining: HomeHealthAlertEnabledMap = {};
        for (const [alertKey, enabled] of Object.entries(channelEdits ?? {})) {
          if (getChannelRowEnabled(persisted, channel, alertKey) !== enabled) {
            remaining[alertKey] = enabled;
          }
        }
        if (Object.keys(remaining).length !== Object.keys(channelEdits ?? {}).length) {
          didChange = true;
        }
        if (Object.keys(remaining).length > 0) {
          next[channel] = remaining;
        }
      }
      return didChange ? next : current;
    });
  }, [user?.alertSettings]);

  const masterEmailEnabled =
    optimisticEdits.EMAIL?.[MASTER_ALERT_KEY] ??
    getChannelRowEnabled(user?.alertSettings, "EMAIL", MASTER_ALERT_KEY);

  const masterSmsEnabled =
    optimisticEdits.SMS?.[MASTER_ALERT_KEY] ??
    getChannelRowEnabled(user?.alertSettings, "SMS", MASTER_ALERT_KEY);

  const emailEnabledByKey = useMemo<HomeHealthAlertEnabledMap>(
    () =>
      Object.fromEntries(
        HOME_HEALTH_ALERT_ROWS.map((row) => [
          row.alertKey,
          optimisticEdits.EMAIL?.[row.alertKey] ??
            getChannelRowEnabled(user?.alertSettings, "EMAIL", row.alertKey),
        ])
      ),
    [user?.alertSettings, optimisticEdits]
  );

  const smsEnabledByKey = useMemo<HomeHealthAlertEnabledMap>(
    () =>
      Object.fromEntries(
        SMS_ROWS.map((row) => [
          row.smsAlertKey,
          optimisticEdits.SMS?.[row.smsAlertKey] ??
            getChannelRowEnabled(user?.alertSettings, "SMS", row.smsAlertKey),
        ])
      ),
    [user?.alertSettings, optimisticEdits]
  );

  function submit(params: { savingTarget: SavingTarget; edits: HomeHealthAlertEnabledMap }) {
    if (!user || updateAlertSettings.isLoading) {
      return;
    }
    const { channel } = params.savingTarget;
    setSavingTarget(params.savingTarget);

    // Carry every pending optimistic edit so a save built on a stale parent
    // snapshot doesn't silently overwrite previously-saved keys.
    const cumulativeChannelEdits: HomeHealthAlertEnabledMap = {
      ...optimisticEdits[channel],
      ...params.edits,
    };
    const cumulativeEdits: AlertChannelEdits = {
      ...optimisticEdits,
      [channel]: cumulativeChannelEdits,
    };
    setOptimisticEdits(cumulativeEdits);

    // Pass every channel's cumulative edits, not just the touched one — the
    // other channel's `existing` state can be stale relative to a
    // just-completed save that hasn't propagated back into `user.alertSettings`
    // yet, and building from `existing` alone would silently re-send (and
    // thus revert) that channel's already-saved value.
    const mergedAlertSettings = buildMergedAlertSettings({
      existing: user.alertSettings,
      edits: cumulativeEdits,
    });

    updateAlertSettings.mutate(
      {
        facilityId: workplaceId,
        performedBy,
        userId: user._id,
        userInfo: {
          alertSettings: mergedAlertSettings,
          notify: user.notify,
        },
      },
      {
        onSuccess: () => onSaved?.(),
        onError: () => {
          setOptimisticEdits((current) => {
            const next = { ...current };
            const channelEdits = { ...next[channel] };
            for (const editedKey of Object.keys(params.edits)) {
              delete channelEdits[editedKey];
            }
            next[channel] = channelEdits;
            return next;
          });
        },
        onSettled: () => setSavingTarget(undefined),
      }
    );
  }

  const handleToggleRow = (channel: AlertChannel, alertKey: string, enabled: boolean) => {
    submit({ savingTarget: { channel, alertKey }, edits: { [alertKey]: enabled } });
  };

  // Writes <channel>.All directly — the backend mirrors this to Knock's
  // channel-level mute, which suppresses every workflow on that channel for
  // this user across every workplace they belong to. A multi-workplace user
  // editing from one workplace's page therefore affects all of them.
  const handleToggleAll = (channel: AlertChannel, enabled: boolean) => {
    submit({
      savingTarget: { channel, alertKey: MASTER_ALERT_KEY },
      edits: { [MASTER_ALERT_KEY]: enabled },
    });
  };

  return (
    <HomeHealthNotificationPreferencesView
      emailEnabledByKey={emailEnabledByKey}
      isLoading={!user}
      masterEmailEnabled={masterEmailEnabled}
      masterSmsEnabled={masterSmsEnabled}
      savingTarget={savingTarget}
      smsEnabledByKey={smsEnabledByKey}
      onToggleAll={handleToggleAll}
      onToggleRow={handleToggleRow}
    />
  );
}
